import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const PricingStrategies = ({ onClose, onSuccess }) => {
  const [strategy, setStrategy] = useState('seasonal');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: 'entry_ticket',
    adjustment: '',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const strategies = [
    { key: 'seasonal', label: 'Seasonal Pricing', icon: '🌟', desc: 'Adjust prices for peak/off seasons' },
    { key: 'bulk', label: 'Bulk Discount', icon: '📦', desc: 'Volume-based pricing adjustments' },
    { key: 'promotional', label: 'Promotional', icon: '🎉', desc: 'Special offers and discounts' },
    { key: 'dynamic', label: 'Dynamic Pricing', icon: '⚡', desc: 'Real-time price optimization' }
  ];

  const categories = [
    { key: 'entry_ticket', label: 'Entry Tickets', icon: '🌳' },
    { key: 'parking_ticket', label: 'Parking', icon: '🅿️' },
    { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
    { key: 'puja_ticket', label: 'Puja Services', icon: '🙏' },
    { key: 'event_ticket', label: 'Events', icon: '🎭' },
    { key: 'other', label: 'Other', icon: '🏛️' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adjustmentValue = parseFloat(formData.adjustment);
      const changeType = strategy === 'bulk' ? 'fixed' : 'percentage';
      
      await axios.put(
        `${API_BASE_URL}/api/tickets/category/${formData.category}/bulk-price`,
        {
          priceChange: adjustmentValue,
          changeType,
          strategy,
          reason: formData.reason,
          dateRange: formData.startDate && formData.endDate ? {
            start: formData.startDate,
            end: formData.endDate
          } : null
        }
      );

      onSuccess(`${strategy.charAt(0).toUpperCase() + strategy.slice(1)} pricing strategy applied successfully!`);
      onClose();
    } catch (error) {
      alert('Error applying pricing strategy: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const selectedStrategy = strategies.find(s => s.key === strategy);
  const selectedCategory = categories.find(c => c.key === formData.category);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>🎯</span>
            Pricing Strategies
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#718096'
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Strategy Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#2d3748' }}>
              Select Pricing Strategy
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              {strategies.map(strat => (
                <button
                  key={strat.key}
                  type="button"
                  onClick={() => setStrategy(strat.key)}
                  style={{
                    padding: '16px 12px',
                    borderRadius: '12px',
                    border: strategy === strat.key ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                    background: strategy === strat.key ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{strat.icon}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{strat.label}</div>
                  <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>{strat.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
              Target Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
            >
              {categories.map(cat => (
                <option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>

          {/* Price Adjustment */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
              Price Adjustment ({strategy === 'bulk' ? '₹' : '%'})
            </label>
            <input
              type="number"
              value={formData.adjustment}
              onChange={(e) => setFormData({...formData, adjustment: e.target.value})}
              required
              step={strategy === 'bulk' ? '1' : '0.1'}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              placeholder={strategy === 'bulk' ? 'e.g., 50 for ₹50 discount' : 'e.g., 10 for 10% increase'}
            />
          </div>

          {/* Date Range (for seasonal/promotional) */}
          {(strategy === 'seasonal' || strategy === 'promotional') && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
              Reason for Change
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                resize: 'vertical'
              }}
              placeholder="Explain the reason for this pricing change..."
            />
          </div>

          {/* Preview */}
          <div style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#2d3748' }}>Preview</h4>
            <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
              <strong>{selectedStrategy?.label}</strong> will be applied to <strong>{selectedCategory?.label}</strong>
              {formData.adjustment && (
                <span> with a {strategy === 'bulk' ? `₹${formData.adjustment}` : `${formData.adjustment}%`} adjustment</span>
              )}
              {formData.startDate && formData.endDate && (
                <span> from {formData.startDate} to {formData.endDate}</span>
              )}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                border: '2px solid #e2e8f0',
                background: 'white',
                color: '#4a5568',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.adjustment}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: loading ? '#9ca3af' : 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || !formData.adjustment ? 'not-allowed' : 'pointer',
                minWidth: '140px'
              }}
            >
              {loading ? '⏳ Applying...' : 'Apply Strategy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingStrategies;